<template>
  <div class="modal fade" id="formModal" tabindex="-1" aria-labelledby="cardFormModalLabel"
    :aria-hidden="!isModalVisible">
    <div class="modal-dialog custom-modal-dialog modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header bg-white border-bottom-0 pb-0">
          <div>
            <h5 class="modal-title fw-bold text-primary mb-1" id="cardFormModalLabel">
              <i :class="isEditing ? 'bi bi-pencil-square' : 'bi bi-plus-circle'" class="me-2"></i>
              {{ isEditing ? "Editar Artículo" : "Nuevo Artículo" }}
            </h5>
            <p class="text-muted small mb-0">
              <span v-if="draftRestored" class="text-success fw-bold">
                <i class="bi bi-save"></i> Borrador restaurado automáticamente
              </span>
              <span v-else>Complete la información para publicar en la web.</span>
            </p>
          </div>
          <button type="button" class="btn-close" @click="closeForm" aria-label="Close"></button>
        </div>

        <div class="modal-body pt-4">
          <form @submit.prevent="submitForm">

            <div v-if="currentStep === 1" class="fade-in">

              <h6 class="text-muted border-bottom pb-2 mb-3 small fw-bold text-uppercase">Información General</h6>
              <div class="row g-3 mb-4">
                <div class="col-md-8">
                  <div class="form-floating">
                    <input type="text" v-model="form.title" class="form-control" id="title" placeholder="Título"
                      required />
                    <label for="title">Título Principal</label>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-floating">
                    <input type="date" v-model="form.date" class="form-control" id="date" required />
                    <label for="date">Fecha</label>
                  </div>
                </div>

                <div class="col-md-8">
                  <div class="form-floating">
                    <select v-model="form.type" class="form-select" id="type" required>
                      <option value="" disabled>Seleccionar...</option>
                      <option v-for="option in categories" :key="option.value" :value="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                    <label for="type">Categoría</label>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-floating">
                    <input type="text" v-model="form.button_text" class="form-control" id="button_text"
                      placeholder="Ej: Ver más" />
                    <label for="button_text">Texto Botón (Opc.)</label>
                  </div>
                </div>
              </div>

              <div class="p-4 bg-light rounded-3 border mb-4">
                <div class="text-center mb-3">
                  <label class="form-label text-uppercase text-muted fw-bold small tracking-wide">
                    Configuración de Destino
                  </label>

                  <div class="d-flex justify-content-center">
                    <div class="btn-group shadow-sm bg-white rounded" role="group" style="min-width: 320px;">
                      <input type="radio" class="btn-check" name="linkType" id="linkType1" value="link"
                        v-model="form.linkType">
                      <label class="btn btn-outline-primary px-4 py-2" for="linkType1">
                        <i class="bi bi-link-45deg fs-5 align-middle"></i> URL Web
                      </label>

                      <input type="radio" class="btn-check" name="linkType" id="linkType2" value="pdf"
                        v-model="form.linkType">
                      <label class="btn btn-outline-primary px-4 py-2" for="linkType2">
                        <i class="bi bi-file-earmark-pdf fs-5 align-middle"></i> PDF
                      </label>
                    </div>
                  </div>
                </div>

                <div class="row" v-if="form.linkType === 'link'">
                  <div class="col-md-10 offset-md-1">
                    <div class="form-floating">
                      <input type="url" v-model="form.link" class="form-control bg-white" id="urlInput"
                        placeholder="https://" />
                      <label for="urlInput" class="text-muted">
                        <i class="bi bi-globe me-1"></i> Pegue el enlace aquí
                      </label>
                    </div>
                  </div>
                </div>

                <div class="row" v-if="form.linkType === 'pdf'">
                  <div class="col-md-10 offset-md-1">
                    <FileUploadPreview label="" id="file" accept="application/pdf" :file="form.file"
                      :preview="form.file ? form.file.name : ''" @file-change="updateFile('file', $event)"
                      no-file-message="Haga clic para subir el PDF" class="clean-upload" />
                  </div>
                </div>
              </div>

              <div class="mb-2">
                <h6 class="text-muted border-bottom pb-2 mb-3 small fw-bold text-uppercase">Multimedia</h6>
                <FileUploadPreview label="Imagen de Portada" id="image" accept="image/*" :file="form.image"
                  :preview="form.imagePreview" @file-change="updateFile('image', $event)"
                  no-file-message="Haga clic o arrastre la imagen principal aquí" class="clean-upload" />
              </div>

              <div class="d-flex justify-content-end mt-4 pt-3 border-top">
                <button class="btn btn-primary px-4 rounded-pill btn-next-step" @click="nextStep"
                  :disabled="!isStep1Valid || validatingSession">
                  <div v-if="validatingSession" class="d-flex align-items-center justify-content-center">
                    <span class="spinner-border me-2" role="status" aria-hidden="true"
                      style="width: 1rem; height: 1rem; border-width: 0.15em;"></span>
                    <span>Verificando...</span>
                  </div>
                  <div v-else>
                    Siguiente <i class="bi bi-chevron-right ms-1"></i>
                  </div>
                </button>
              </div>
            </div>

            <div v-if="currentStep === 2" class="fade-in">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="fw-bold mb-0">Contenido Detallado</h6>
                <span class="badge bg-light text-dark border">Editor de Texto</span>
              </div>

              <div class="mb-3 editor-wrapper">
                <quill-editor ref="quillEditor" v-model:content="form.content" :theme="'snow'"
                  class="form-control quill-editor-container" @ready="onEditorReady" @text-change="onTextChange"
                  toolbar="full"></quill-editor>
              </div>

              <div class="d-flex justify-content-between mt-4 pt-3 border-top">
                <button class="btn btn-outline-secondary px-4 rounded-pill" @click="prevStep">
                  <i class="bi bi-chevron-left me-1"></i> Atrás
                </button>
                <button type="submit" class="btn btn-success px-5 rounded-pill" :disabled="!isStep2Valid">
                  <i class="bi bi-check-lg me-1"></i>
                  {{ isEditing ? 'Guardar Cambios' : 'Publicar Artículo' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { Modal } from "bootstrap";
import { QuillEditor } from "@vueup/vue-quill";
import Quill from "quill";
import FileUploadPreview from "@/components/shared/FileUploadPreview.vue";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { buildFormData } from "@/utils/formDataBuilder.js";
import { validateToken } from "@/services/api/authService";

export default {
  components: {
    FileUploadPreview,
    QuillEditor,
  },
  props: {
    view: {
      type: Object,
      required: true,
    },
    header: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const isModalVisible = ref(false);
    const showArticleForm = ref(false);
    const currentStep = ref(1);
    const isEditing = ref(false);
    const validatingSession = ref(false);
    const draftRestored = ref(false);

    const quillEditor = ref(null);
    const editorReady = ref(false);
    const contentHasText = ref(false);

    const categories = ref([
      { value: 1, label: "AUTOS" },
      { value: 2, label: "MOTOS" },
      { value: 3, label: "CAMIONES" },
      { value: 4, label: "MAQUINARIA AGRICOLA" },
      { value: 5, label: "SOCIOS" },
      { value: 6, label: "ASESORIA LEGAL" },
      { value: 7, label: "ASESORIA IMPOSITIVA" },
      { value: 8, label: "ASESORIA REGISTRAL" },
      { value: 9, label: "CAPACITACIONES" },
    ]);

    const form = ref({
      title: "",
      date: "",
      linkType: "link",
      link: "",
      file: null,
      image: null,
      type: "",
      priority: 0,
      content: "",
      button_text: "",
      view_id: props.view.id,
    });

    const storageKey = computed(() => {
      const id = props.header?.id || 'new';
      return `article_draft_view_${props.view.id}_${id}`;
    });

    const saveDraft = () => {
      if (form.value.title || form.value.content) {
        const draftData = { ...form.value, file: null, image: null, imagePreview: null };
        localStorage.setItem(storageKey.value, JSON.stringify(draftData));
      }
    };

    const loadDraft = () => {
      const savedDraft = localStorage.getItem(storageKey.value);
      if (savedDraft && !isEditing.value) {
        try {
          const parsed = JSON.parse(savedDraft);
          form.value = { ...form.value, ...parsed };
          draftRestored.value = true;
          setTimeout(() => { draftRestored.value = false }, 5000);
        } catch (e) {
          console.error("Error al cargar borrador", e);
        }
      }
    };

    const clearDraft = () => {
      localStorage.removeItem(storageKey.value);
    };

    watch(form, () => { saveDraft(); }, { deep: true });

    const isStep1Valid = computed(() => {
      return (
        form.value.title &&
        form.value.date &&
        form.value.type &&
        (form.value.linkType === "link" ? form.value.link : form.value.file) &&
        form.value.image
      );
    });

    const isStep2Valid = computed(() => {
      return contentHasText.value;
    });

    watch(
      () => props.header,
      (newHeader) => {
        if (newHeader) {
          isEditing.value = true;
          form.value = {
            title: newHeader.title,
            content: newHeader.content,
            link: newHeader.link,
            file: newHeader.file || null,
            image: newHeader.image || null,
            type: newHeader.type || "",
            priority: newHeader.priority || 0,
            date: newHeader.date,
            imagePreview: newHeader.image,
            linkType: newHeader.link ? "link" : newHeader.file ? "pdf" : "link",
            button_text: newHeader.button_text || "",
            view_id: newHeader.view_id || props.view.id,
          };
        } else {
          isEditing.value = false;
          form.value = {
            title: "", date: "", linkType: "link", link: "", file: null,
            image: null, imagePreview: "", type: "", priority: 0, content: "",
            button_text: "", view_id: props.view.id,
          };
        }
      },
      { immediate: true }
    );

    const nextStep = async () => {
      validatingSession.value = true;
      const isValid = await validateToken();
      validatingSession.value = false;

      if (isValid) {
        currentStep.value = 2;
      }
    };

    const prevStep = () => {
      currentStep.value = 1;
    };

    const updateFile = (type, file) => {
      form.value[type] = file;
    };

    const closeForm = () => {
      isModalVisible.value = false;
      emit("close");
      const modalElement = document.getElementById("formModal");
      const modalInstance = Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    };

    const submitForm = () => {
      if (quillEditor.value) {
        const quillInstance = quillEditor.value.getQuill();
        const contentDelta = quillInstance.getContents();
        const quillTemp = new Quill(document.createElement("div"));
        quillTemp.setContents(contentDelta);
        form.value.content = quillTemp.root.innerHTML;
      }

      if (form.value.linkType === "link") {
        form.value.file = null;
      } else if (form.value.linkType === "pdf") {
        form.value.link = null;
      }

      const formData = buildFormData(form.value, form.value.linkType);

      emit("article-submitted", formData);
      clearDraft();
      closeForm();
    };

    onMounted(() => {
      const modalElement = document.getElementById("formModal");
      const modalInstance = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      });
      modalInstance.show();
      isModalVisible.value = true;
      loadDraft();
    });

    const onEditorReady = () => {
      editorReady.value = true;
      if (quillEditor.value && form.value.content) {
        const quillInstance = quillEditor.value.getQuill();
        quillInstance.clipboard.dangerouslyPasteHTML(form.value.content);
      }
    };

    const onTextChange = () => {
      if (quillEditor.value) {
        const quillInstance = quillEditor.value.getQuill();
        contentHasText.value = quillInstance.getText().trim().length > 0;
      }
    };

    return {
      showArticleForm,
      currentStep,
      form,
      categories,
      closeForm,
      nextStep,
      prevStep,
      updateFile,
      submitForm,
      quillEditor,
      onEditorReady,
      onTextChange,
      editorReady,
      isStep1Valid,
      isStep2Valid,
      isEditing,
      validatingSession,
      draftRestored
    };
  },
};
</script>

<style scoped>
.custom-modal-dialog {
  max-width: 700px;
  margin: 1.75rem auto;
}

.tracking-wide {
  letter-spacing: 1px;
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

.btn-next-step {
  min-width: 140px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

::v-deep .quill-editor-container {
  height: 500px !important;
  display: flex;
  flex-direction: column;
}

::v-deep .ql-toolbar {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

::v-deep .ql-container {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  font-size: 16px;
  border-color: #dee2e6;
}

::v-deep .clean-upload .border {
  background-color: #f8f9fa !important;
  border: 2px dashed #dee2e6 !important;
  color: #6c757d !important;
  transition: all 0.3s;
}

::v-deep .clean-upload .border:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
}

@media (max-width: 768px) {
  .custom-modal-dialog {
    max-width: 95%;
    margin: 10px auto;
  }

  .modal-content {
    border-radius: 0;
    min-height: 100vh;
  }

  ::v-deep .quill-editor-container {
    height: 350px !important;
  }
}
</style>